import {
  Component, input, output, OnInit, effect, ChangeDetectionStrategy, inject,
  QueryList, ViewChildren, ViewChild, ElementRef, signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DocumentFormComponent } from '../components/document-form/document-form.component';
import { FormMode } from 'app/core/constants/form-mode';
import { ViewerComponent, DocViewerItem } from '../components/viewer/viewer.component';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { Global } from '@services/core/Global';
import { ImageOptimizerService } from 'app/core/service/utils/image-optimizer.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-document-group',
  templateUrl: './document-group.component.html',
  styleUrls: ['./document-group.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, ReactiveFormsModule, DocumentFormComponent, ViewerComponent, ListCardComponent],
})
export class DocumentGroupComponent implements OnInit {
  public appSettings = inject(AppSettings);
  private imageOptimizer = inject(ImageOptimizerService);
  public formMode = FormMode;

  documents = input.required<any>();
  mode = input(FormMode.EDIT);
  partyOverviewService = input<any>();
  /** Subir al servidor al elegir archivo (p. ej. evidencias de daño ya guardado). */
  immediateUpload = input(false);
  /** Ya está dentro de un panel: no volver a envolver cada categoría. */
  embedded = input(false);
  /** Lista continua, sin encabezados ni paneles por categoría. */
  flat = input(false);
  /**
   * forms = formularios apilados (claims / legacy).
   * table = checklist estilo aprobación (incorporación): Subir / Ver / Reemplazar.
   */
  layout = input<'forms' | 'table'>('forms');

  formChange = output<FormGroup>();
  onDocumentUpdate = output<void>();
  onUploadFile = output<{
    uuid: string;
    file: File;
    origin?: string;
    expirationDate?: string | null;
    done?: (ok: boolean) => void;
  }>();
  onDeleteDocument = output<string>();

  documentsForm!: FormGroup;
  @ViewChildren(DocumentFormComponent) documentForms?: QueryList<DocumentFormComponent>;
  @ViewChild('tableFileInput') tableFileInput?: ElementRef<HTMLInputElement>;
  baseUrl = Global.url;

  isViewerOpen = false;
  viewerItems: DocViewerItem[] = [];
  activeViewerId: string | number = 0;

  /** uuids en upload (layout table). */
  private busySet = signal<Set<string>>(new Set());
  private pendingDoc: any = null;

  private Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2200,
    timerProgressBar: true
  });

  constructor(private fb: FormBuilder) {
    effect(() => {
      if (this.documents()) {
        this.buildDocuments();
      }
    });
  }

  ngOnInit(): void {
    this.buildForm();
  }

  /** Desktop tabla o mobile cards con acciones directas (sin document-form). */
  get useChecklistActions(): boolean {
    return this.layout() === 'table';
  }

  get useDesktopTable(): boolean {
    return this.layout() === 'table' && !this.appSettings.isMobile();
  }

  canUploadOrReplace(doc: any): boolean {
    if (this.mode() !== this.formMode.VIEW) return true;
    const status = String(doc?.statusSlug ?? '').toUpperCase();
    return status === 'OBSERVED' || (status === 'PENDING' && !!String(doc?.reviewComment ?? '').trim());
  }

  buildForm() {
    this.documentsForm = this.fb.group({
      documents: this.fb.array([])
    });
  }

  get documentsArray(): FormArray {
    return this.documentsForm.get('documents') as FormArray;
  }

  buildDocuments() {
    if (!this.documents()?.categories) return;
    const flatDocs = this._getFlatDocuments();
    const existingMap = this._getExistingControlsMap();
    this.documentsArray.clear();
    flatDocs.forEach((doc: any) => this._processDocumentForm(doc, existingMap));
    this.formChange.emit(this.documentsForm);
  }

  private _getFlatDocuments(): any[] {
    const list: any[] = [];
    this.documents().categories.forEach((c: any) => c.documents && list.push(...c.documents));
    return list;
  }

  private _getExistingControlsMap(): Map<string, FormGroup> {
    const map = new Map<string, FormGroup>();
    for (let i = 0; i < this.documentsArray.length; i++) {
      const fg = this.documentsArray.at(i) as FormGroup;
      const uuid = fg.get('uuid')?.value;
      if (uuid) map.set(uuid, fg);
    }
    return map;
  }

  private _processDocumentForm(doc: any, existingMap: Map<string, FormGroup>) {
    let fg = existingMap.get(doc.uuid);
    if (fg) {
      this._updateExistingFormGroup(fg, doc);
    } else {
      fg = this.createDocumentForm(doc);
    }
    doc.formIndex = this.documentsArray.length;
    this.documentsArray.push(fg);
  }

  private _updateExistingFormGroup(fg: FormGroup, doc: any) {
    const hasFile = !!fg.get('file')?.value;
    const extra = hasFile ? {} : { fileName: doc.fileName ?? null, mimeType: doc.mimeType ?? null, fileSize: doc.fileSize ?? null };
    fg.patchValue({
      filePath: doc.filePath ?? null,
      thumbnailPath: doc.thumbnailPath ?? null,
      statusSlug: doc.statusSlug ?? 'PENDING',
      allowExpiration: !!doc.allowExpiration,
      expirationDate: fg.get('expirationDate')?.value ?? doc.expirationDate ?? null,
      id: doc.uuid ?? doc.id ?? fg.get('id')?.value,
      ...extra
    }, { emitEvent: false });
    const expCtrl = fg.get('expirationDate');
    if (expCtrl) {
      expCtrl.setValidators(doc.allowExpiration ? Validators.required : []);
      expCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  createDocumentForm(doc: any): FormGroup {
    return this.fb.group({
      uuid: [doc.uuid ?? null],
      id: [doc.uuid ?? doc.id ?? null],
      processSlug: [doc.processSlug, Validators.required],
      ownerSlug: [doc.ownerSlug, Validators.required],
      categorySlug: [doc.categorySlug, Validators.required],
      typeSlug: [doc.typeSlug, Validators.required],
      partyId: [doc.partyId ?? this.documents().partyId],
      file: [null],
      fileName: [doc.fileName ?? null],
      filePath: [doc.filePath ?? null],
      thumbnailPath: [doc.thumbnailPath ?? null],
      mimeType: [doc.mimeType ?? null],
      fileSize: [doc.fileSize ?? null],
      expirationDate: [doc.expirationDate ?? null, doc.allowExpiration ? Validators.required : []],
      allowExpiration: [!!doc.allowExpiration],
      statusSlug: [doc.statusSlug ?? 'PENDING'],
    });
  }

  validateImage(doc: any): boolean {
    return doc.filePath && (
      (doc.mimeType && doc.mimeType.includes('image'))
      || (doc.fileName && !!doc.fileName.match(/\.(jpeg|jpg|gif|png|webp)$/i))
    );
  }

  isPdf(doc: any): boolean {
    if (!doc) return false;
    const path = String(doc.filePath ?? '').toLowerCase();
    const name = String(doc.fileName ?? '').toLowerCase();
    const mime = String(doc.mimeType ?? '').toLowerCase();
    return path.endsWith('.pdf') || name.endsWith('.pdf') || mime.includes('pdf');
  }

  pickDocumentFile(doc: any): void {
    if (this.useChecklistActions) {
      this.startChecklistUpload(doc);
      return;
    }
    this.findDocumentForm(doc)?.pickFile();
  }

  /** Abre file picker del checklist (tabla / mobile table). */
  startChecklistUpload(doc: any): void {
    if (this.mode() === this.formMode.VIEW || this.isDocumentBusy(doc)) return;
    this.pendingDoc = doc;
    const input = this.tableFileInput?.nativeElement;
    if (!input) return;
    input.value = '';
    input.click();
  }

  async onChecklistFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const doc = this.pendingDoc;
    input.value = '';
    this.pendingDoc = null;
    if (!file || !doc?.uuid) return;

    const expirationDate = await this.askExpirationIfNeeded(doc);
    if (doc.allowExpiration && expirationDate === null) return;

    await this.emitChecklistUpload(doc, file, expirationDate);
  }

  /** Pedir fecha si el tipo exige vencimiento. null = canceló. */
  private async askExpirationIfNeeded(doc: any): Promise<string | null | undefined> {
    if (!doc?.allowExpiration) return undefined;
    const prefill = doc.expirationDate
      ? String(doc.expirationDate).slice(0, 10)
      : '';
    const result = await Swal.fire({
      title: 'Fecha de vencimiento',
      html: `<p class="small text-muted mb-2 text-start">Requerida para <b>${doc.docTypeName || 'este documento'}</b>.</p>`,
      input: 'date',
      inputValue: prefill,
      showCancelButton: true,
      confirmButtonText: 'Continuar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => (!value ? 'Indique la fecha de vencimiento' : null)
    });
    if (!result.isConfirmed) return null;
    return String(result.value || '').slice(0, 10) || null;
  }

  private async emitChecklistUpload(doc: any, file: File, expirationDate?: string | null): Promise<void> {
    const uuid = doc.uuid;
    this.setBusy(uuid, true);
    try {
      let finalFile = file;
      if (file.type.startsWith('image/')) {
        try {
          finalFile = (await this.imageOptimizer.optimize(file)) || file;
        } catch {
          finalFile = file;
        }
      }
      await new Promise<void>((resolve) => {
        this.onUploadFile.emit({
          uuid,
          file: finalFile,
          origin: doc.origin,
          expirationDate: expirationDate ?? null,
          done: () => resolve()
        });
      });
    } catch {
      this.Toast.fire({ icon: 'error', title: 'No se pudo preparar el archivo' });
    } finally {
      this.setBusy(uuid, false);
    }
  }

  private setBusy(uuid: string, busy: boolean): void {
    const next = new Set(this.busySet());
    if (busy) next.add(uuid);
    else next.delete(uuid);
    this.busySet.set(next);
  }

  confirmDeleteDocument(doc: any, event?: Event): void {
    event?.stopPropagation();
    if (!doc?.uuid || doc.isRequired !== false || this.isDocumentBusy(doc)) return;
    Swal.fire({
      title: '¿Eliminar documento?',
      text: 'Se quitará el requisito.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí'
    }).then((r) => {
      if (r.isConfirmed) this.onDeleteDocument.emit(doc.uuid);
    });
  }

  onDocumentCardClick(doc: any): void {
    if (this.hasDocumentFile(doc)) {
      this.openViewer(doc.uuid || doc.id);
      return;
    }
    if (this.mode() !== this.formMode.VIEW) {
      this.pickDocumentFile(doc);
    }
  }

  isDocumentBusy(doc: any): boolean {
    if (this.useChecklistActions) {
      return this.busySet().has(doc?.uuid);
    }
    return !!this.findDocumentForm(doc)?.isBusy();
  }

  hasDocumentFile(doc: any): boolean {
    return !!(doc?.filePath || this.localPreview(doc) || this.isLocalPdf(doc));
  }

  documentThumbSrc(doc: any): string | null {
    const local = this.localPreview(doc);
    if (local) return local;
    if (this.validateImage(doc)) {
      const path = doc.thumbnailPath || doc.filePath;
      return path ? this.baseUrl + path : null;
    }
    return null;
  }

  showPdfThumb(doc: any): boolean {
    return this.isPdf(doc) || this.isLocalPdf(doc);
  }

  private localPreview(doc: any): string | null {
    if (this.useChecklistActions) return null;
    const url = this.findDocumentForm(doc)?.previewUrl();
    return url && url !== 'pdf' ? url : null;
  }

  private isLocalPdf(doc: any): boolean {
    if (this.useChecklistActions) return false;
    return this.findDocumentForm(doc)?.previewUrl() === 'pdf';
  }

  private findDocumentForm(doc: any): DocumentFormComponent | undefined {
    return this.documentForms?.find((form) => {
      const current = form.document();
      return current?.uuid === doc?.uuid || current?.docTypeCode === doc?.docTypeCode;
    });
  }

  openViewer(clickedId?, event?: Event) {
    event?.stopPropagation();
    this.viewerItems = [];
    this.documents().categories.forEach((category: any) => {
      category.documents.forEach((doc: any) => {
        const pdf = this.isPdf(doc);
        if (this.validateImage(doc) || pdf) {
          this.viewerItems.push({
            id: doc.uuid,
            imageUrl: Global.url + doc.filePath,
            title: doc.docTypeName,
            status: 'success',
            mimeType: doc.mimeType ?? (pdf ? 'application/pdf' : null),
            fileName: doc.fileName ?? null,
            kind: pdf ? 'pdf' : 'image'
          });
        }
      });
    });
    this.launchViewer(clickedId);
  }

  private launchViewer(clickedId?) {
    if (this.viewerItems.length > 0) {
      this.activeViewerId = clickedId ?? this.viewerItems[0].id;
      this.isViewerOpen = true;
    } else {
      this.showNoImagesAlert();
    }
  }

  private showNoImagesAlert() {
    Swal.fire({
      icon: 'info',
      title: 'Sin documentos',
      text: 'Este socio no cuenta con documentos visualizables subidos aún.',
      confirmButtonText: 'Entendido'
    });
  }
}
